import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  CircularProgress,
  Divider,
  Paper
} from '@mui/material';
import { Trip, Address } from '@/types';
import { SignUpData, ParticipantData } from '@/types/signup';
import LoadingSpinner from '../ui/LoadingSpinner';
import CompanionSection from './CompanionSection';
import PersonalInfoSection from './PersonalInfoSection';
import AddressSection from './AddressSection';
import SkiPreferencesSection from './SkiPreferencesSection';
import TravelPreferencesSection from './TravelPreferencesSection';
import SignUpTypeSelector from './SignUpTypeSelector';
import TripInfoCard from './TripInfoCard';
import { CheckCircle, Home, Send } from '@mui/icons-material';
import { useI18n } from '@/hooks/useI18n';

interface TripSignUpFormProps {
  trip: Trip;
  onSubmit: (data: SignUpData) => void;
  loading?: boolean;
  submitted?: boolean;
  onBackToHome?: () => void;
}

const TripSignUpForm: React.FC<TripSignUpFormProps> = ({
  trip,
  onSubmit,
  loading = false,
  submitted = false,
  onBackToHome
}) => {
  const { t, formatDate } = useI18n();
  const [signUpType, setSignUpType] = useState<'individual' | 'group'>('individual');
  const [formData, setFormData] = useState<SignUpData>({
    tripId: trip.id,
    isGroupSignUp: false,
    primaryParticipant: {
      name: '',
      dateOfBirth: '',
      isSkiClubMember: false,
      preferredSkiPass: ''
    },
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'Deutschland'
    },
    preferredApartmentTypeId: '',
    preferredTransportationId: '',
    additionalNotes: '',
    companions: []
  });

  const handleSignUpTypeChange = (_: React.MouseEvent<HTMLElement>, newType: 'individual' | 'group') => {
    if (newType) {
      setSignUpType(newType);
      setFormData(prev => ({
        ...prev,
        isGroupSignUp: newType === 'group'
      }));
    }
  };

  const handlePrimaryParticipantChange = (field: keyof ParticipantData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      primaryParticipant: {
        ...prev.primaryParticipant,
        [field]: value
      }
    }));
  };

  const handleContactChange = (field: 'email' | 'phone', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (address: Address) => {
    setFormData(prev => ({
      ...prev,
      address
    }));
  };

  const handlePreferencesChange = (field: 'preferredApartmentTypeId' | 'preferredTransportationId' | 'additionalNotes', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCompanionsChange = (companions: ParticipantData[]) => {
    setFormData(prev => ({
      ...prev,
      companions
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isFormValid = () => {
    const { primaryParticipant, email, phone, address, preferredApartmentTypeId, preferredTransportationId } = formData;
    
    return (
      primaryParticipant.name &&
      email &&
      phone &&
      primaryParticipant.dateOfBirth &&
      address.street &&
      address.city &&
      address.postalCode &&
      address.country &&
      preferredApartmentTypeId &&
      preferredTransportationId
    );
  };

  // Success Screen
  if (submitted) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Paper 
          elevation={3}
          sx={{ 
            p: 6, 
            textAlign: 'center',
            bgcolor: 'success.light',
            border: 2,
            borderColor: 'success.main'
          }}
        >
          <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
          
          <Typography variant="h4" sx={{ mb: 2, color: 'success.dark', fontWeight: 'bold' }}>
            🎉 {t('signup.signUpSuccessful')}!
          </Typography>
          
          <Typography variant="h6" sx={{ mb: 3, color: 'success.main' }}>
            {t('signup.welcomeMessage')} {trip.name}!
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, color: 'text.primary', maxWidth: 600, mx: 'auto' }}>
            {t('signup.thankYouMessage')}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, color: 'text.primary', maxWidth: 600, mx: 'auto' }}>
            {t('signup.confirmationEmail')}
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', fontStyle: 'italic' }}>
            {t('signup.getReadyMessage')} {t('trip.from')} {formatDate(trip.startDate)} {t('trip.to')} {formatDate(trip.endDate)}!
          </Typography>
          
          {onBackToHome && (
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<Home />}
              onClick={onBackToHome}
              sx={{ 
                minWidth: 200,
                py: 1.5
              }}
            >
              {t('common.backToHome')}
            </Button>
          )}
        </Paper>
      </Box>
    );
  }

  // Loading Screen  
  if (loading) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Paper elevation={3} sx={{ p: 6, textAlign: 'center' }}>
          <LoadingSpinner />
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            {t('signup.submittingSignUp')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('common.loading')} {trip.name}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Grid container spacing={3}>
        {/* Trip Info Header */}
        <Grid size={{ xs: 12 }}>
          <TripInfoCard trip={trip} />
        </Grid>

        {/* Sign-up Form */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 1 }}>✍️ {t('signup.signUpForThisTrip')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('signup.fillRequiredInfo')}
              </Typography>

              {/* Sign-up Type Selector */}
              <SignUpTypeSelector
                signUpType={signUpType}
                onSignUpTypeChange={handleSignUpTypeChange}
              />

              <Box component="form" onSubmit={handleSubmit}>
                {/* Personal Information Section */}
                <PersonalInfoSection
                  signUpType={signUpType}
                  primaryParticipant={formData.primaryParticipant}
                  email={formData.email}
                  phone={formData.phone}
                  onPrimaryParticipantChange={handlePrimaryParticipantChange}
                  onEmailChange={(value) => handleContactChange('email', value)}
                  onPhoneChange={(value) => handleContactChange('phone', value)}
                />

                {/* Address Section */}
                <AddressSection
                  address={formData.address}
                  onAddressChange={handleAddressChange}
                />

                {/* Ski Preferences Section */}
                <SkiPreferencesSection
                  signUpType={signUpType}
                  primaryParticipant={formData.primaryParticipant}
                  trip={trip}
                  onPrimaryParticipantChange={handlePrimaryParticipantChange}
                />

                {/* Divider before Travel Preferences */}
                <Divider sx={{ my: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('signup.travelPreferences')}
                  </Typography>
                </Divider>

                {/* Travel Preferences Section */}
                <TravelPreferencesSection
                  signUpType={signUpType}
                  trip={trip}
                  preferredApartmentTypeId={formData.preferredApartmentTypeId}
                  preferredTransportationId={formData.preferredTransportationId}
                  noApartmentTypesAvailable={trip.apartmentTypes.length === 0}
                  noTransportationOptionsAvailable={trip.transportationOptions.length === 0}
                  onApartmentTypeChange={(value) => handlePreferencesChange('preferredApartmentTypeId', value)}
                  onTransportationChange={(value) => handlePreferencesChange('preferredTransportationId', value)}
                />

                {/* Companions Section (only for group registration) */}
                {signUpType === 'group' && (
                  <CompanionSection
                    companions={formData.companions}
                    onCompanionsChange={handleCompanionsChange}
                    trip={trip}
                  />
                )}

                {/* Submit Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    size="large" 
                    disabled={!isFormValid() || loading}
                    sx={{ minWidth: 200, py: 1.5 }}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                  >
                    {loading 
                      ? t('signup.submittingSignUp') 
                      : t('signup.submitSignUpRequest')
                    }
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TripSignUpForm;
